import type { SelectChangeEvent, SxProps, Theme } from "@mui/material";
import {
  Box,
  Chip,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import type {
  AssistantMappingCard,
  AssistantMappingChoices,
  AssistantMappingSource,
  AssistantMappingTarget,
} from "../../../../api/types/assistantTypes";

interface Props {
  mapping: AssistantMappingCard;
  choices: AssistantMappingChoices;
  disabled: boolean;
  onChange: (targetKey: string, sourceKey: string | null) => void;
}

const notMappedValue = "";

const sourceSelectSx: SxProps<Theme> = (theme) => ({ width: theme.spacing(25) });

const MappingCardTable = ({ mapping, choices, disabled, onChange }: Props) => {
  const usedSources = new Set(Object.values(choices));
  const unusedSources = mapping.sources.filter((source) => !usedSources.has(source.key));

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Our field</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Limits</TableCell>
              <TableCell>Incoming column</TableCell>
              <TableCell>Example</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mapping.targets.map((target) => (
              <MappingRow
                key={target.key}
                target={target}
                selectedSource={choices[target.key] ?? null}
                sources={mapping.sources}
                disabled={disabled}
                onChange={onChange}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {unusedSources.length > 0 && (
        <Typography color="textSecondary" sx={{ mt: 1 }}>
          Not used from the file: {unusedSources.map((source) => source.label).join(", ")}
        </Typography>
      )}
    </Box>
  );
};

interface MappingRowProps {
  target: AssistantMappingTarget;
  selectedSource: string | null;
  sources: AssistantMappingSource[];
  disabled: boolean;
  onChange: (targetKey: string, sourceKey: string | null) => void;
}

const MappingRow = ({ target, selectedSource, sources, disabled, onChange }: MappingRowProps) => {
  const sample = sources.find((source) => source.key === selectedSource)?.sample;
  const handleChange = (event: SelectChangeEvent<string>) =>
    onChange(target.key, event.target.value === notMappedValue ? null : event.target.value);

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography>
            {target.label}
            {target.required && <RequiredFieldMarker />}
          </Typography>
          {target.isKey && <Chip label="Id" color="primary" />}
        </Stack>
      </TableCell>
      <TableCell>{target.type}</TableCell>
      <TableCell>{target.constraints}</TableCell>
      <TableCell>
        <Select
          value={selectedSource ?? notMappedValue}
          onChange={handleChange}
          displayEmpty
          disabled={disabled}
          error={target.required && selectedSource === null}
          sx={sourceSelectSx}
        >
          <MenuItem value={notMappedValue}>
            <em>Not mapped</em>
          </MenuItem>
          {sources.map((source) => (
            <MenuItem key={source.key} value={source.key}>
              {source.label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell sx={{ wordBreak: "break-word" }}>{sample}</TableCell>
    </TableRow>
  );
};

export default MappingCardTable;
